import {createAsyncThunk, createSlice} from '@reduxjs/toolkit';
import {botApi, RequestStatus, transcriptApi, widgetAppearanceApi} from "../../../api/apiConfiguration";
import {
    FullTranscriptUi,
    GetUserTranscriptRequest,
    LocaleTag,
    SupportedTimezone,
    WidgetAppearanceLocaleVariantUi
} from "../../../api/generated";
import {RootState} from "../../../app/store";

interface TranscriptState {
    transcript?: FullTranscriptUi
    widgetAppearanceSettings?: WidgetAppearanceLocaleVariantUi
    botTimezone?: SupportedTimezone
    transcriptRequestStatus: RequestStatus
    widgetAppearanceSettingsRequestStatus: RequestStatus
    botTimezoneRequestStatus: RequestStatus
}

const initialState: TranscriptState = {
    transcriptRequestStatus: RequestStatus.NONE,
    widgetAppearanceSettingsRequestStatus: RequestStatus.NONE,
    botTimezoneRequestStatus: RequestStatus.NONE
}

export const fetchTranscriptById = createAsyncThunk<FullTranscriptUi, GetUserTranscriptRequest>(
    'transcript/fetchTranscriptById',
    (request) => {
        return transcriptApi.getUserTranscript(request)
    }
);

export const fetchWidgetAppearanceSettings = createAsyncThunk<WidgetAppearanceLocaleVariantUi, string>(
    'transcript/fetchWidgetAppearanceSettings',
    async (payload) => {
        const response: any = await widgetAppearanceApi.getWidgetAppearanceSettingsByPublicBotId({publicBotId: payload});

        if (Array.isArray(response?.localeVariants)) {
            const resolvedVariant = response.localeVariants.find((variant: any) => variant?.locale === LocaleTag.EnGb)
                || response.localeVariants[0]
                || {};

            return resolvedVariant as WidgetAppearanceLocaleVariantUi;
        }

        return response as WidgetAppearanceLocaleVariantUi;
    }
);

export const fetchTranscriptBotTimezone = createAsyncThunk<SupportedTimezone, string>(
    'transcript/fetchTranscriptBotTimezone',
    async (publicBotId) => {
        const bot = await botApi.getBotById({publicId: publicBotId});
        return bot.timezone;
    }
);

export const transcriptSlice = createSlice({
    name: 'transcript',
    initialState,
    reducers: {},
    extraReducers: builder => {
        builder.addCase(fetchTranscriptById.fulfilled, (state, {payload}) => {
            state.transcript = payload;
            state.transcriptRequestStatus = RequestStatus.RESOLVED
        });
        builder.addCase(fetchTranscriptById.pending, (state) => {
            state.transcriptRequestStatus = RequestStatus.PENDING
        });
        builder.addCase(fetchTranscriptById.rejected, (state) => {
            state.transcriptRequestStatus = RequestStatus.REJECTED
        });
        builder.addCase(fetchWidgetAppearanceSettings.fulfilled, (state, {payload}) => {
            state.widgetAppearanceSettings = payload;
            state.widgetAppearanceSettingsRequestStatus = RequestStatus.RESOLVED
        });
        builder.addCase(fetchWidgetAppearanceSettings.pending, (state) => {
            state.widgetAppearanceSettingsRequestStatus = RequestStatus.PENDING
        });
        builder.addCase(fetchWidgetAppearanceSettings.rejected, (state) => {
            state.widgetAppearanceSettingsRequestStatus = RequestStatus.REJECTED
        });
        builder.addCase(fetchTranscriptBotTimezone.fulfilled, (state, {payload}) => {
            state.botTimezone = payload;
            state.botTimezoneRequestStatus = RequestStatus.RESOLVED;
        });
        builder.addCase(fetchTranscriptBotTimezone.pending, (state) => {
            state.botTimezoneRequestStatus = RequestStatus.PENDING;
        });
        builder.addCase(fetchTranscriptBotTimezone.rejected, (state) => {
            state.botTimezoneRequestStatus = RequestStatus.REJECTED;
        });
    }
})

export const _ = transcriptSlice.actions;

export const transcriptSelector = (state: RootState): FullTranscriptUi | undefined => state.transcript.transcript;
export const transcriptRequestStatusSelector = (state: RootState): RequestStatus => state.transcript.transcriptRequestStatus;

export const widgetAppearanceSettingsSelector = (state: RootState): WidgetAppearanceLocaleVariantUi | undefined => state.transcript.widgetAppearanceSettings;
export const widgetAppearanceSettingsRequestStatusSelector = (state: RootState): RequestStatus => state.transcript.widgetAppearanceSettingsRequestStatus;
export const transcriptBotTimezoneSelector = (state: RootState): SupportedTimezone | undefined => state.transcript.botTimezone;
export const transcriptBotTimezoneRequestStatusSelector = (state: RootState): RequestStatus => state.transcript.botTimezoneRequestStatus;
